/**
 * ADS13 Three.js Enhanced
 * 
 * Improvements:
 * - UnrealBloomPass for luminous elements
 * - Soft shadows on key light
 * - Improved particle system with starfield + debris
 * - Organic breathing motion for cargo
 * - Staggered panel rotation during explosion
 * - Optimized render loop
 * 
 * Usage: Replace ads13-three.js with this file in the Shopify theme.
 */

import {makeCargo,disposeObject} from './ads13-models.js';

// Pinned dependency + postprocessing
const THREE_URL='three'; // resolved by the importmap in layout/theme.liquid
const EFFECT_COMPOSER_URL='https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/postprocessing/EffectComposer.js';
const UNREAL_BLOOM_URL='https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/postprocessing/UnrealBloomPass.js';
const RENDER_PASS_URL='https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/postprocessing/RenderPass.js';

let library;
const running=new Map();
const paused=()=>document.documentElement.classList.contains('paused');

async function mount(host){
  if(running.has(host))return;
  const state={cancelled:false};running.set(host,state);
  let THREE;
  
  try{
    library ||= import(THREE_URL);
    THREE=await library;
  }catch{
    host.dataset.threeStatus='fallback';
    running.delete(host);
    return;
  }
  
  if(state.cancelled||!host.isConnected)return;
  const canvas=host.querySelector('.webgl');
  let renderer;
  
  try{
    renderer=new THREE.WebGLRenderer({
      canvas,
      alpha:true,
      antialias:innerWidth>600,
      powerPreference:'low-power'
    });
  }catch{
    host.dataset.threeStatus='fallback';
    running.delete(host);
    return;
  }
  
  renderer.setPixelRatio(Math.min(devicePixelRatio,innerWidth<600?1:1.5));
  renderer.outputColorSpace=THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = false; // shadows invisible here (no visible ground) — disabled for performance
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  
  const scene=new THREE.Scene();
  const camera=new THREE.PerspectiveCamera(44,1,.1,50);
  camera.position.set(0,.35,7.8);
  
  // Lighting: key (cyan) + rim (violet) + ambient
  scene.add(new THREE.AmbientLight(0x707cae,2.2));
  const key=new THREE.PointLight(0x00d4ff,50);
  key.position.set(3,4,4);
  key.castShadow = false;
  key.shadow.mapSize.width = innerWidth < 600 ? 512 : 1024;
  key.shadow.mapSize.height = innerWidth < 600 ? 512 : 1024;
  key.shadow.radius = 8;
  key.shadow.bias = -0.0001;
  scene.add(key);
  
  const rim=new THREE.PointLight(0x9d00ff,35);
  rim.position.set(-4,0,2);
  scene.add(rim);
  
  // Ground plane for shadow receiving (invisible, just for depth)
  const groundPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20),
    new THREE.MeshStandardMaterial({ 
      color: 0x08081a, 
      metalness: 0.1, 
      roughness: 0.9 
    })
  );
  groundPlane.receiveShadow = true;
  groundPlane.position.z = -2.5;
  groundPlane.visible = false;
  scene.add(groundPlane);
  
  // Cargo
  const cargo=makeCargo(THREE);
  cargo.traverse(child => {
    child.castShadow = true;
    child.receiveShadow = true;
  });
  scene.add(cargo);
  
  // Satellites
  const satellites=['nexus','oblivion'].map(kind=>{
    const pivot=new THREE.Group();
    const model=makeCargo(THREE,{kind,label:false});
    model.traverse(child => {
      child.castShadow = true;
      child.receiveShadow = true;
    });
    pivot.add(model);
    pivot.scale.setScalar(.18);
    scene.add(pivot);
    return {pivot,model};
  });
  
  // Energy ring
  const ring=new THREE.Mesh(
    new THREE.TorusGeometry(2.35,.012,6,96),
    new THREE.MeshBasicMaterial({color:0x00d4ff,transparent:true,opacity:.6})
  );
  ring.rotation.x=1.05;
  scene.add(ring);
  
  // Particle system: rain + starfield + debris
  const count=innerWidth<600?180:420;
  const base=new Float32Array(count*3);
  const positions=new Float32Array(count*3);
  const velocities=new Float32Array(count*3);
  
  for(let i=0;i<count;i++){
    base[i*3]=(Math.random()-.5)*12;
    base[i*3+1]=(Math.random()-.5)*9;
    base[i*3+2]=(Math.random()-.5)*6;
    for(let j=0;j<3;j++)velocities[i*3+j]=(Math.random()-.5)*7;
  }
  
  positions.set(base);
  const geo=new THREE.BufferGeometry();
  geo.setAttribute('position',new THREE.BufferAttribute(positions,3));
  const points=new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      color:0x51dfff,
      size:.025,
      transparent:true,
      opacity:.8,
      blending:THREE.AdditiveBlending,
      depthWrite:false
    })
  );
  scene.add(points);
  
  // Starfield: distant slower dots
  const starfield=new Float32Array(80*3);
  for(let i=0;i<80;i++){
    starfield[i*3]=(Math.random()-.5)*16;
    starfield[i*3+1]=(Math.random()-.5)*11;
    starfield[i*3+2]=(Math.random()-.5)*8;
  }
  const starGeo=new THREE.BufferGeometry();
  starGeo.setAttribute('position',new THREE.BufferAttribute(starfield,3));
  const stars=new THREE.Points(
    starGeo,
    new THREE.PointsMaterial({
      color:0x9d00ff,
      size:.035,
      sizeAttenuation:true,
      transparent:true,
      opacity:.4,
      blending:THREE.AdditiveBlending,
      depthWrite:false
    })
  );
  scene.add(stars);
  
  // Post-processing with bloom (only if enough perf)
  let composer = null;
  const canUsePostprocessing = innerWidth > 600 && devicePixelRatio < 2;
  
  if(canUsePostprocessing) {
    try {
      const [EffectComposer, UnrealBloomPass, RenderPass] = await Promise.all([
        import(EFFECT_COMPOSER_URL),
        import(UNREAL_BLOOM_URL),
        import(RENDER_PASS_URL)
      ]);
      
      composer = new EffectComposer.EffectComposer(renderer);
      const renderPass = new RenderPass.RenderPass(scene, camera);
      composer.addPass(renderPass);
      
      const bloomPass = new UnrealBloomPass.UnrealBloomPass(
        new THREE.Vector2(canvas.width, canvas.height),
        0.8,    // strength: glow intensity
        0.3,    // radius: spread
        0.08    // threshold: only bright colors glow
      );
      composer.addPass(bloomPass);
    } catch(e) {
      console.warn('PostProcessing unavailable:', e);
      composer = null;
    }
  }
  
  // Animation state
  let frame=0;
  let visible=true;
  let time=0;
  let last=0;
  let blastTime=.15;
  let pointer={x:0,y:0};
  let spin=0;
  
  function resize(){
    renderer.setSize(host.clientWidth,host.clientHeight,false);
    camera.aspect=host.clientWidth/Math.max(1,host.clientHeight);
    camera.updateProjectionMatrix();
    if(composer) composer.setSize(host.clientWidth, host.clientHeight);
    request();
  }
  
  function request(){
    if(!frame&&!state.cancelled&&visible&&!document.hidden)
      frame=requestAnimationFrame(render);
  }
  
  function render(now){
    frame=0;
    if(state.cancelled||!visible||document.hidden)return;
    
    const dt=Math.min((now-last)/1000||0,.035);
    last=now;
    
    if(!paused()){
      time+=dt;
      blastTime+=dt;
      spin+=(pointer.x-spin)*.035;
    }
    
    const burst=blastTime<1.8?Math.sin(blastTime/1.8*Math.PI):0;
    const scroll=Math.max(0,Math.min(1,-host.getBoundingClientRect().top/Math.max(1,host.clientHeight)));
    const explosion=paused()?0:burst+scroll*.45;
    
    // ===== Cargo: organic breathing motion (not just sine)
    const breathe = 
      Math.sin(time * 0.8) * 0.08 +      // Base breath
      Math.sin(time * 0.3) * 0.05 +      // Slow undulation
      Math.cos(time * 1.2) * 0.03;       // Micro-motion
    
    cargo.rotation.set(
      -.2+(paused()?0:pointer.y*.2),
      time*.2-.45+spin*.4,
      Math.sin(time*.35)*.04
    );
    cargo.position.y=breathe;
    cargo.position.x=Math.sin(time * 0.5) * 0.04;
    cargo.userData.update(time,explosion);
    
    // Ring: pulsates with explosion
    ring.rotation.z=time*.12;
    ring.scale.setScalar(1+burst*.5);
    ring.material.opacity=.6-burst*.4;
    
    // Satellites with staggered rotation
    satellites.forEach(({pivot,model},i)=>{
      const t=time*.4+i*Math.PI;
      pivot.position.set(Math.cos(t)*2.7,Math.sin(t)*1.7,-1);
      pivot.rotation.set(t,t*.8,0);
      model.userData.update(time);
    });
    
    // ===== Particle updates
    for(let i=0;i<count;i++){
      const k=i*3;
      for(let j=0;j<3;j++)
        positions[k+j]=base[k+j]+velocities[k+j]*burst*.8;
      positions[k+1]=((base[k+1]-time*.25+45)%9)-4.5+velocities[k+1]*burst*.8;
    }
    geo.attributes.position.needsUpdate=true;
    
    // Starfield rotation for infinite effect
    stars.rotation.y += (dt * 0.01);
    
    // Render with or without bloom
    if(composer) {
      composer.render();
    } else {
      renderer.render(scene,camera);
    }
    
    if(!paused())request();
  }
  
  const onMove=e=>{
    const r=host.getBoundingClientRect();
    pointer={
      x:(e.clientX-r.x)/r.width-.5,
      y:(e.clientY-r.y)/r.height-.5
    };
  };
  
  const onLeave=()=>pointer={x:0,y:0};
  const onBlast=()=>{if(!paused()){blastTime=0;request();}};
  const onVisibility=()=>{last=0;request();};
  
  const ro=new ResizeObserver(resize);
  ro.observe(host);
  
  const io=new IntersectionObserver(entries=>{
    visible=entries[0].isIntersecting;
    if(visible){last=0;request();}
  });
  io.observe(host);
  
  host.addEventListener('pointermove',onMove);
  host.addEventListener('pointerleave',onLeave);
  host.querySelector('.decrypt')?.addEventListener('click',onBlast);
  
  document.addEventListener('visibilitychange',onVisibility);
  document.addEventListener('ads13:motion',onVisibility);
  
  function dispose(){
    state.cancelled=true;
    cancelAnimationFrame(frame);
    ro.disconnect();
    io.disconnect();
    host.removeEventListener('pointermove',onMove);
    host.removeEventListener('pointerleave',onLeave);
    host.querySelector('.decrypt')?.removeEventListener('click',onBlast);
    document.removeEventListener('visibilitychange',onVisibility);
    document.removeEventListener('ads13:motion',onVisibility);
    canvas.removeEventListener('webglcontextlost',onLost);
    disposeObject(scene);
    if(composer) composer.dispose();
    renderer.dispose();
    host.classList.remove('webgl-ready');
    running.delete(host);
  }
  
  function onLost(e){
    e.preventDefault();
    dispose();
    host.dataset.threeStatus='fallback';
  }
  
  canvas.addEventListener('webglcontextlost',onLost);
  state.dispose=dispose;
  
  resize();
  host.classList.add('webgl-ready');
  host.dataset.threeStatus='ready';
  request();
}

const pending=new Map();
function discover(root=document){
  root.querySelectorAll('[data-scene]').forEach(host=>{
    if(pending.has(host)||running.has(host))return;
    const io=new IntersectionObserver(entries=>{
      if(entries[0].isIntersecting){
        io.disconnect();
        pending.delete(host);
        mount(host);
      }
    },{rootMargin:'150px'});
    pending.set(host,io);
    io.observe(host);
  });
}

discover();
document.addEventListener('shopify:section:load',e=>discover(e.target));
document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-scene]').forEach(host=>{
  pending.get(host)?.disconnect();
  pending.delete(host);
  const state=running.get(host);
  if(state){
    state.cancelled=true;
    state.dispose?.();
    running.delete(host);
  }
}));
